'use client';

import { getLocalTimeZone, today } from '@internationalized/date';
import * as React from 'react';
import {
    Button as AriaButton,
    Calendar as AriaCalendar,
    CalendarCell as AriaCalendarCell,
    CalendarCellProps as AriaCalendarCellProps,
    CalendarGrid as AriaCalendarGrid,
    CalendarGridBody as AriaCalendarGridBody,
    CalendarGridBodyProps as AriaCalendarGridBodyProps,
    CalendarGridHeader as AriaCalendarGridHeader,
    CalendarGridHeaderProps as AriaCalendarGridHeaderProps,
    CalendarGridProps as AriaCalendarGridProps,
    CalendarHeaderCell as AriaCalendarHeaderCell,
    CalendarHeaderCellProps as AriaCalendarHeaderCellProps,
    Heading as AriaHeading,
    RangeCalendar as AriaRangeCalendar,
    RangeCalendarStateContext as AriaRangeCalendarStateContext,
    composeRenderProps,
    useLocale
} from 'react-aria-components';

import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from 'lib/cn';
import { buttonClassNames } from '~/components/ui';

const Calendar = AriaCalendar;

const RangeCalendar = AriaRangeCalendar;

const CalendarHeading = (props: React.HTMLAttributes<HTMLElement>) => {
    let { direction } = useLocale();

    return (
        <header
            className="tw-mb-1 tw-flex tw-w-full tw-items-center tw-gap-1 tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-px-1 tw-pb-1"
            {...props}
        >
            <AriaButton
                slot="previous"
                className={cn(
                    buttonClassNames({ color: 'light' }),
                    'tw-size-7 tw-bg-transparent tw-p-0 tw-opacity-50',
                    /* Hover */
                    'data-[hovered]:tw-opacity-100'
                )}
            >
                {direction === 'rtl' ? (
                    <FontAwesomeIcon icon={faChevronRight} aria-hidden className="tw-size-3" />
                ) : (
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden className="tw-size-3" />
                )}
            </AriaButton>
            <AriaHeading className="tw-mb-0 tw-grow tw-text-center tw-text-sm tw-font-medium tw-text-accents-9" />
            <AriaButton
                slot="next"
                className={cn(
                    buttonClassNames({ color: 'light' }),
                    'tw-size-7 tw-bg-transparent tw-p-0 tw-opacity-50',
                    /* Hover */
                    'data-[hovered]:tw-opacity-100'
                )}
            >
                {direction === 'rtl' ? (
                    <FontAwesomeIcon icon={faChevronLeft} aria-hidden className="tw-size-3" />
                ) : (
                    <FontAwesomeIcon icon={faChevronRight} aria-hidden className="tw-size-3" />
                )}
            </AriaButton>
        </header>
    );
};

const CalendarGrid = ({ className, ...props }: AriaCalendarGridProps) => (
    <AriaCalendarGrid className={cn('tw-border-separate tw-border-spacing-0.5', className)} {...props} />
);

const CalendarGridHeader = ({ ...props }: AriaCalendarGridHeaderProps) => <AriaCalendarGridHeader {...props} />;

const CalendarHeaderCell = ({ className, ...props }: AriaCalendarHeaderCellProps) => (
    <AriaCalendarHeaderCell
        className={cn(
            'tw-w-9 tw-rounded-md tw-text-center tw-text-[0.8rem] tw-font-normal tw-text-accents-7',
            className
        )}
        {...props}
    />
);

const CalendarGridBody = ({ className, ...props }: AriaCalendarGridBodyProps) => (
    <AriaCalendarGridBody className={cn('[&>tr>td]:tw-p-0', className)} {...props} />
);

const CalendarCell = ({ className, ...props }: AriaCalendarCellProps) => {
    const isRange = Boolean(React.useContext(AriaRangeCalendarStateContext));
    return (
        <AriaCalendarCell
            className={composeRenderProps(className, (composedClassName, renderProps) =>
                cn(
                    'tw-relative tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-xl tw-p-0 tw-text-sm tw-font-normal tw-transition-colors data-[hovered]:tw-bg-accents-2 data-[hovered]:tw-text-accents-9',
                    /* Disabled */
                    renderProps.isDisabled && 'tw-text-accents-6 tw-opacity-50',
                    /* Selected */
                    renderProps.isSelected &&
                        'tw-bg-successLight tw-text-success data-[focused]:tw-bg-successLight/40 data-[focused]:tw-text-success',
                    /* Hover */
                    renderProps.isHovered &&
                        renderProps.isSelected &&
                        (renderProps.isSelectionStart || renderProps.isSelectionEnd || !isRange) &&
                        'data-[hovered]:tw-bg-accents-3 data-[hovered]:tw-text-success',
                    /* Selection Start/End */
                    renderProps.isSelected &&
                        isRange &&
                        !renderProps.isSelectionStart &&
                        !renderProps.isSelectionEnd &&
                        'tw-bg-accent tw-text-accent-foreground tw-rounded-none',
                    /* Outside Month */
                    renderProps.isOutsideMonth &&
                        'tw-text-muted-foreground data-[selected]:tw-bg-accent/50 data-[selected]:tw-text-muted-foreground tw-opacity-50 data-[selected]:tw-opacity-30',
                    /* Current Date */
                    renderProps.date.compare(today(getLocalTimeZone())) === 0 &&
                        // !renderProps.isSelected &&
                        'tw-border tw-border-solid tw-border-accents-5',
                    /* Unavailable Date */
                    renderProps.isUnavailable && 'tw-cursor-default tw-text-accents-2',
                    renderProps.isInvalid &&
                        'tw-bg-destructive tw-text-destructive-foreground data-[focused]:tw-bg-destructive data-[hovered]:tw-bg-destructive data-[focused]:tw-text-destructive-foreground data-[hovered]:tw-text-destructive-foreground',
                    composedClassName
                )
            )}
            {...props}
        />
    );
};

export {
    Calendar,
    CalendarCell,
    CalendarGrid,
    CalendarGridBody,
    CalendarGridHeader,
    CalendarHeaderCell,
    CalendarHeading,
    RangeCalendar
};
